import { useAtomValue } from 'jotai';
import { selectAtom } from 'jotai/utils';
import { useEffect } from 'react';

import { chronicleDeviceStateAtom } from '@/atoms/chronicle';
import { syncHardwareProfileMetadata } from '@/utils/analytics/hardware-profile-metadata';

/** Only the identity fields the profile write reads. The explicit equality
 * function is what makes that a real cut: without it the fresh object literal
 * is never equal to the last one, so the selector would still emit — and
 * re-render its consumer — on every battery / RSSI / connection-status tick. */
const profileFieldsAtom = selectAtom(
  chronicleDeviceStateAtom,
  (state) => ({
    deviceName: state.deviceName,
    serialNumber: state.serialNumber,
    firmwareVersion: state.firmwareVersion,
  }),
  (a, b) =>
    a.deviceName === b.deviceName &&
    a.serialNumber === b.serialNumber &&
    a.firmwareVersion === b.firmwareVersion
);

/**
 * Forwards the linked Heidi Remote's serial number and firmware version to the
 * Amplitude user profile and the Intercom contact (HAR-874).
 *
 * The device-state atom is the single funnel every relevant scenario already
 * passes through: cold-start restore from the backend `hardware_device` record,
 * fresh pairing, BLE (re)connect, and the post-OTA firmware report. The write
 * itself is deduped per value pair, so a chatty atom costs nothing.
 *
 * `enabled` must carry the same auth + Chronicle-flag gate as the linked-device
 * restore. The provider's Tauri state listener populates the atom on any desktop
 * build where Rust reports a Remote — flag included — so without the gate the
 * flag could not stop this egress, and a signed-out session could write to an
 * unidentified profile.
 */
export function useSyncHardwareProfileMetadata(enabled: boolean) {
  const { deviceName, serialNumber, firmwareVersion } = useAtomValue(profileFieldsAtom);

  useEffect(() => {
    if (!enabled) return;
    syncHardwareProfileMetadata({ deviceName, serialNumber, firmwareVersion });
  }, [enabled, deviceName, serialNumber, firmwareVersion]);
}
